import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import { Observable, firstValueFrom } from 'rxjs';

// PascalCase throughout — the API's Newtonsoft config keeps C# property casing on the wire.
// Breeze entities are lowercase, but this is raw controller output. Camel-casing a field here
// silently yields undefined rather than an error.

/**
 * One color a ProductType offers, with whatever supplier imagery is staged for it.
 *
 * The server returns exactly the colors Uniformax declares — never the full staged set. Staging
 * holds roughly 3,500 SanMar colors Proximity does not sell, and none of them may reach a buyer.
 * See OfferingImageService.
 */
export interface OfferingColorImagesDto {
  /** Uniformax's own color name — what the buyer sees. */
  ColorName: string;
  /** SanMar's 14-character catalogue token. Null when nothing matched. */
  SupplierColorCode: string | null;
  /** SanMar's unabbreviated name — "Sand Dune/ Loden" against the token "SndDune/Ld". */
  StandardColorName: string | null;
  /** Approximate Pantone, for a true swatch rather than a hex guessed from a name. */
  ApproximatePms: string | null;
  /** "StandardColorName" or "ColorCode" — which field carried the match. */
  MatchedOn: string | null;
  /** Set when several supplier codes shared one standard name. The server picked one. */
  AmbiguousCodes: string | null;
  SwatchUrl: string | null;
  ThumbnailUrl: string | null;
  MainImageUrl: string | null;
  /**
   * "Feed", "Stored", or null. A "Stored" image is Proximity's own byte copy hung off the
   * ProductType, which is a STYLE image - the same one for every color - so it is worth saying
   * so rather than letting it pass as the color's own photograph.
   */
  MainImageSource: string | null;
  ModelBackUrl: string | null;
  /**
   * Every other staged view of this color - side, three-quarter, flat lays, and anything the
   * feed published without a label. Ordered by role then sequence. Empty, never null.
   *
   * The four named fields above were all the offering view ever read, so these sat staged and
   * unreachable.
   */
  AdditionalUrls: string[];
  /** Whether any catalog feed covers this manufacturer. False for 76 of our 78. */
  IsFeedEnabled: boolean;
  HasImages: boolean;
}

/** One requested id's answer in a batch call - a ProductTypeId or a ProductTypeConfigId. */
export interface OfferingImagesForIdDto {
  Id: string;
  Colors: OfferingColorImagesDto[];
}

/**
 * The one place a screen gets product imagery from. Every image display is moving onto this
 * service and off the old SupplierImage bytes in Breeze (getProductTypeImages + ImageFns) - Jeff,
 * 2026-10-18. New image code calls this; do not add callers of the old path.
 */
@Injectable({ providedIn: 'root' })
export class OfferingImageApiService {
  /** Must match OfferingImageService.MaxBatchSize on the server. */
  static readonly MAX_BATCH = 500;

  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}Supplier`;

  /**
   * A feed URL is already absolute. Proximity's own stored-image URL comes back relative
   * (img/producttype/{id}), because in development the UI and the API are on different ports.
   */
  static absoluteUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    return /^https?:\/\//i.test(url) ? url : environment.apiUrl + url;
  }

  /**
   * The picture for a list tile, absolute: the first color with its own feed photograph, else any main
   * image, else a swatch. Feed first because a color the feed does not match gets the STORED style
   * image, which can show a color the config does not sell - CTY1-8001-581, 2026-11-02: "Orange/Black"
   * sorts before "Safety Orange", has no feed match, and put the stored Safety Yellow photo on the tile.
   */
  static tileImageUrl(colors: OfferingColorImagesDto[] | undefined): string | null {
    const withMain = colors?.find((c) => c.MainImageUrl && c.MainImageSource === 'Feed') ?? colors?.find((c) => c.MainImageUrl);
    return OfferingImageApiService.absoluteUrl(withMain?.MainImageUrl ?? colors?.find((c) => c.ThumbnailUrl)?.ThumbnailUrl);
  }

  getForProductType(productTypeId: string): Observable<OfferingColorImagesDto[]> {
    const params = new HttpParams().set('productTypeId', productTypeId);
    return this.http.get<OfferingColorImagesDto[]>(`${this.baseUrl}/OfferingImages`, { params });
  }

  /**
   * The supplier's spec sheet (sizing guide PDF) for one ProductType, or null. Only SanMar
   * publishes one today. The URL is the supplier's own and already absolute.
   */
  async getSpecSheetUrl(productTypeId: string): Promise<string | null> {
    const params = new HttpParams().set('productTypeId', productTypeId);
    const r = await firstValueFrom(this.http.get<{ Url: string | null }>(`${this.baseUrl}/OfferingSpecSheet`, { params }));
    return r?.Url ?? null;
  }

  /**
   * Images for many ProductTypes - for a list or grid of styles. The server answers a whole
   * chunk in five queries; asking one ProductType at a time costs five queries each.
   *
   * Keyed by lower-cased ProductTypeId, because the wire and Breeze need not agree on Guid case.
   * Every requested id is a key; one with no images maps to an empty array.
   */
  getForProductTypes(productTypeIds: string[]): Promise<Map<string, OfferingColorImagesDto[]>> {
    return this.postBatch('OfferingImagesBatch', productTypeIds);
  }

  /**
   * Images for many ProductTypeConfigs - for anything that shows one tile per configuration, such
   * as a program menu. Each config gets only the colors its own products carry; asking by
   * ProductType would show a color the config does not sell. Same keying as getForProductTypes.
   */
  getForProductTypeConfigs(productTypeConfigIds: string[]): Promise<Map<string, OfferingColorImagesDto[]>> {
    return this.postBatch('OfferingImagesForConfigsBatch', productTypeConfigIds);
  }

  private async postBatch(action: string, rawIds: string[]): Promise<Map<string, OfferingColorImagesDto[]>> {
    const ids = [...new Set(rawIds.filter((id) => !!id).map((id) => id.toLowerCase()))];
    const result = new Map<string, OfferingColorImagesDto[]>(ids.map((id) => [id, []]));
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += OfferingImageApiService.MAX_BATCH) {
      chunks.push(ids.slice(i, i + OfferingImageApiService.MAX_BATCH));
    }
    const answers = await Promise.all(
      chunks.map((chunk) => firstValueFrom(this.http.post<OfferingImagesForIdDto[]>(`${this.baseUrl}/${action}`, chunk))),
    );
    for (const row of answers.flat()) {
      result.set(row.Id.toLowerCase(), row.Colors ?? []);
    }
    return result;
  }
}
